import React, { useEffect, useRef, useState } from 'react';
import '../pages/ManageSMRTCOP.css';
import './SubscriptionPanel.css';
import { useModules } from '../context/ModulesContext';
import DefaultModuleModal from './DefaultModuleModal';
import InfoModal from './InfoModal';

type OverrideState = boolean | null;

interface ToolEntitlement {
  enabled: boolean;
  [key: string]: any;
}

interface AddOn {
  key: string;
  name: string;
  description: string;
  category: string;
}

interface OrgEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: {
    orgID: string;
    companyName: string;
    displayName: string;
    planName: string;
    planFeatures: Record<string, ToolEntitlement>;
    overrideFeatures: Record<string, ToolEntitlement>;
    lastUpdated: string;
    billingAdmin?: string;
  } | null;
  addOns: AddOn[];
  onOverrideChange?: (orgID: string, key: string, value: boolean | null) => void;
}

export default function OrgEditModal({
  isOpen,
  onClose,
  subscription,
  addOns,
  onOverrideChange,
}: OrgEditModalProps) {
  const { approvedKeys, labelFor } = useModules();
  const [localOverrides, setLocalOverrides] = useState<Record<string, OverrideState>>({});
  const [errorFeature, setErrorFeature] = useState<string | null>(null);
  const [loadingFeature, setLoadingFeature] = useState<string | null>(null);
  const [showDefaultModuleModal, setShowDefaultModuleModal] = useState(false);
  const [dependencyMessage, setDependencyMessage] = useState<string | null>(null);
  const initializedForOrgID = useRef<string | null>(null);

  useEffect(() => {
    if (!subscription) {
      initializedForOrgID.current = null;
      return;
    }
    if (initializedForOrgID.current === subscription.orgID) return;

    const init: Record<string, OverrideState> = {};
    for (const key of Object.keys(subscription.overrideFeatures)) {
      init[key] = subscription.overrideFeatures[key].enabled;
    }
    setLocalOverrides(init);
    setErrorFeature(null);
    setLoadingFeature(null);
    initializedForOrgID.current = subscription.orgID;
  }, [subscription]);

  if (!isOpen || !subscription) return null;

  const { orgID, companyName, displayName, planName, planFeatures, billingAdmin } = subscription;

  const effectiveState = (key: string): boolean => {
    const override = localOverrides[key];
    if (override !== null && override !== undefined) return override;
    return planFeatures?.[key]?.enabled === true;
  };

  const setOverride = async (featureKey: string, newValue: OverrideState) => {
    const prevValue = localOverrides[featureKey] ?? null;
    setLoadingFeature(featureKey);
    setLocalOverrides((prev) => ({ ...prev, [featureKey]: newValue }));
    setErrorFeature(null);

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/add_on`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ add_on: featureKey, value: newValue, orgID }),
      });
      if (!res.ok) throw new Error('Failed to update add-on');
      await res.json();
      onOverrideChange?.(orgID, featureKey, newValue);
    } catch (err) {
      console.error(`Failed to set override for ${featureKey}:`, err);
      setLocalOverrides((prev) => ({ ...prev, [featureKey]: prevValue }));
      setErrorFeature(featureKey);
    } finally {
      setLoadingFeature(null);
    }
  };

  const handleToggle = (featureKey: string, checked: boolean) => {
    if (featureKey === 'chatbot' && !checked) {
      setShowDefaultModuleModal(true);
      return;
    }
    if (featureKey === 'smrtDocs' && checked && !effectiveState('profiles')) {
      setDependencyMessage('SMRT Docs requires Profiles. Please activate Profiles first.');
      return;
    }
    // TOSP sits on top of both. It reads a tenant's lease through SMRT Docs,
    // and the tenant portal is gated on a completed profile — turned on alone
    // it comes up empty rather than broken, which is harder to diagnose.
    if (featureKey === 'tOSP' && checked) {
      const missing = [
        !effectiveState('profiles') && 'Profiles',
        !effectiveState('smrtDocs') && 'SMRT Docs',
      ].filter(Boolean) as string[];
      if (missing.length > 0) {
        setDependencyMessage(
          `TOSP requires ${missing.join(' and ')}. Please activate ${
            missing.length > 1 ? 'them' : 'it'
          } first.`,
        );
        return;
      }
    }
    // Naming every dependant, not just the first one found — "deactivate SMRT
    // Docs first" is a dead end when TOSP is the one actually holding it.
    if (featureKey === 'profiles' && !checked) {
      const needed = [
        effectiveState('smrtDocs') && 'SMRT Docs',
        effectiveState('tOSP') && 'TOSP',
      ].filter(Boolean) as string[];
      if (needed.length > 0) {
        setDependencyMessage(
          `Profiles is required by ${needed.join(' and ')}. Please deactivate ${
            needed.length > 1 ? 'them' : 'it'
          } first.`,
        );
        return;
      }
    }
    if (featureKey === 'smrtDocs' && !checked && effectiveState('tOSP')) {
      setDependencyMessage('SMRT Docs is required by TOSP. Please deactivate TOSP first.');
      return;
    }
    if (featureKey === 'sMRTCRMTIX' && checked && !effectiveState('smrtTasks')) {
      setDependencyMessage('SMRT CRMTIX requires SMRT Tasks. Please activate SMRT Tasks first.');
      return;
    }
    if (featureKey === 'smrtTasks' && !checked && effectiveState('sMRTCRMTIX')) {
      setDependencyMessage('SMRT Tasks is required by SMRT CRMTIX. Please deactivate SMRT CRMTIX first.');
      return;
    }
    setOverride(featureKey, checked);
  };

  const handleChatbotDisable = async (selectedModule: string) => {
    setShowDefaultModuleModal(false);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/default-module`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgID, defaultModule: selectedModule }),
      });
      if (!res.ok) throw new Error('Failed to set default module');
    } catch (err) {
      console.error('Failed to set default module:', err);
      setErrorFeature('chatbot');
      return;
    }
    await setOverride('chatbot', false);
  };

  const enabledModuleKeys = approvedKeys.filter((k) =>
    k !== 'chatbot' && effectiveState(k),
  );

  const titleName = displayName || companyName;

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: '560px', maxHeight: '85vh', overflowY: 'auto' }}>
        <div className="smrtdocs-modal-header" style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1rem' }}>Edit {titleName}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#cbd5e1',
              fontSize: '1.2rem',
              cursor: 'pointer',
              lineHeight: 1,
              padding: '2px 6px',
            }}
          >
            ✕
          </button>
        </div>
        <div className="smrtdocs-modal-body" style={{ padding: '10px 16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 3, columnGap: 10, fontSize: '0.9rem', marginBottom: 10 }}>
            <span style={{ color: '#9aa0a6' }}>Organization Name</span>
            <span>{displayName || '—'}</span>
            <span style={{ color: '#9aa0a6' }}>Legal Entity Name</span>
            <span>{companyName || '—'}</span>
            <span style={{ color: '#9aa0a6' }}>Billing Admin</span>
            <span>{billingAdmin || '—'}</span>
            <span style={{ color: '#9aa0a6' }}>Plan</span>
            <span>{planName || '—'}</span>
          </div>

          <p style={{ margin: '0 0 4px', fontSize: '0.85rem', fontWeight: 600 }}>Modules</p>

          <div className="addon-toggles" style={{ marginTop: 0 }}>
            {approvedKeys.map((key) => {
              const addon = addOns.find((a) => a.key === key) ?? {
                key,
                name: labelFor(key),
                description: '',
                category: '',
              };
              const isEffectivelyOn = effectiveState(addon.key);
              const isLoading = loadingFeature === addon.key;
              const errored = errorFeature === addon.key;

              return (
                <div
                  className={`addon-toggle ${errored ? 'error' : ''}`}
                  key={addon.key}
                >
                  <label className="switch-label">
                    <span className="addon-name">
                      {labelFor(addon.key)}
                    </span>
                    <div className="toggle-controls">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={isEffectivelyOn}
                          disabled={isLoading}
                          onChange={(e) => handleToggle(addon.key, e.target.checked)}
                          style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </label>
                </div>
              );
            })}
          </div>

        </div>
      </div>

      <DefaultModuleModal
        isOpen={showDefaultModuleModal}
        onClose={() => setShowDefaultModuleModal(false)}
        onConfirm={handleChatbotDisable}
        enabledModules={enabledModuleKeys}
      />

      <InfoModal
        isOpen={!!dependencyMessage}
        onClose={() => setDependencyMessage(null)}
        title="Module Dependency"
        message={dependencyMessage || ''}
      />
    </div>
  );
}
